import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Input,
  Button,
} from "@material-tailwind/react";
import {
  FaSearch,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { PROXY, fetchData, setCache, getCache } from "../../lib/utils"; // adjust path

const ScheduledSubjectsModal = ({ open, handleOpen, accessToken }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // ✅ define both value and setter
  const [totalPages, setTotalPages] = useState(1);
  const visibleSubjects = subjects.filter((item) => item?.status !== "reject");

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "capitalize bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium";
      case "reserved":
        return "capitalize bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium";
      case "completed":
        return "capitalize bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium";
      default:
        return "capitalize bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium";
    }
  };
  const getStatusLabel = (status) => t(status?.toLowerCase() || "pending");
  const statusLegend = [
    { key: "pending", label: t("pending") },
    { key: "reserved", label: t("reserved") },
    { key: "completed", label: t("completed") },
  ];

  // Fetch data from server
  useEffect(() => {
    if (!open) return;
    const cacheKey = `reserved-courses-${page}-${limit}-${searchTerm}`;
    const cached = getCache(cacheKey);

    if (cached) {
      setSubjects(cached.results || []);
      setTotalPages(cached.totalPages || 1);
      return;
    }

    const fetchSubjects = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = `${PROXY}/reserved-courses?q=${encodeURIComponent(
          searchTerm
        )}&page=${page}&limit=${limit}`;

        const data = await fetchData(url, accessToken);
        setSubjects(data.results || []);
        setTotalPages(data.totalPages || 1);
        setCache(cacheKey, data, 1); // cache for 1 hours
      } catch (err) {
        setError(err.message);
        setSubjects([]);
      } finally {
        setLoading(false);
      }
    };

    const t = setTimeout(fetchSubjects, 400); // debounce
    return () => clearTimeout(t);
  }, [open, searchTerm, page, limit, accessToken]);

  const renderLang = (lang) => {
    const normalized = String(lang || "").trim().toLowerCase();
    switch (normalized) {
      case "dari":
        return t("dari");
      case "arabic":
        return "Arabic";
      case "pashto":
        return t("pashto");
      case "english":
        return t("english");
      default:
        if (!normalized) return "";
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }
  };

  return (
    <Dialog open={open} size="xl" handler={handleOpen} className="max-h-[90vh]">
      <DialogHeader className="flex flex-row items-start justify-between gap-3 border-b border-gray-100 pr-3 sm:pr-4">
        <div className="min-w-0">
          <span className="block text-base font-semibold text-gray-900 truncate">
            {t("attachments.table-heading")}
          </span>
          <span className="block text-xs text-gray-500 mt-1">
            {t("Search-Subjects")}
          </span>
        </div>
        <Button
          variant="text"
          color="red"
          onClick={handleOpen}
          className="text-red-500 hover:text-red-700 text-xl font-bold p-2 self-start min-w-0"
        >
          X
        </Button>
      </DialogHeader>

      <DialogBody divider className="max-h-[70vh] overflow-y-auto pb-4">
        {/* Top Section: Search */}
        <div className="w-full mb-6">
          {/* Search Input */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
            <Input
              label={t("Search-Subjects")}
              value={searchTerm}
              color="indigo"
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // reset to first page on new search
              }}
              className="flex-1 h-10"
              containerProps={{ className: "w-full" }}
            />
            <Button
              variant="gradient"
              color="indigo"
              className="w-full sm:w-auto flex items-center justify-center gap-2 h-10 px-4 min-w-[120px]"
              onClick={() => setPage(1)} // trigger fetch immediately
            >
              <FaSearch size={14} className="shrink-0" />
              <span className="whitespace-nowrap">{t("Search-course")}</span>
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-gray-600">
          {statusLegend.map((status) => (
            <span
              key={status.key}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1"
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  status.key === "pending"
                    ? "bg-yellow-500"
                    : status.key === "reserved"
                    ? "bg-blue-500"
                    : "bg-green-500"
                }`}
              />
              {status.label}
            </span>
          ))}
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden space-y-3">
          {loading ? (
            <div className="text-center py-6 text-gray-500">{t("loading")}</div>
          ) : error ? (
            <div className="text-center py-6 text-red-500">{error}</div>
          ) : visibleSubjects.length > 0 ? (
            visibleSubjects.map((item, idx) => (
              <div
                key={`${item._id || item.course || item.subject}-${idx}`}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {item.course || item.subject}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("University-Name")}: {item.university}
                    </p>
                  </div>
                  <span className={getStatusColor(item.status)}>
                    {getStatusLabel(item.status)}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700">
                  <div>{t("Faculty-Name")}: {item.faculty}</div>
                  <div>{t("Department-Name")}: {item.department}</div>
                  <div>{t("Taught-in-semester")}: {item.semester}</div>
                  <div>{t("Credits")}: {item.credits}</div>
                  <div>{t("Teacher-Name")}: {item.teacherName || item.teacher}</div>
                  <div>{t("Language")}: {renderLang(item.lang)}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="flex flex-col items-center gap-2">
                <FaSearch className="text-gray-400" />
                <span>{t("No-subjects-found")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Table Section */}
        <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-100 text-gray-900 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 border">{t("University-Name")}</th>
                <th className="px-4 py-2 border">{t("Faculty-Name")}</th>
                <th className="px-4 py-2 border">{t("Department-Name")}</th>
                <th className="px-4 py-2 border">{t("Subject-Name")}</th>
                <th className="px-4 py-2 border">{t("Taught-in-semester")}</th>
                <th className="px-4 py-2 border">{t("Credits")}</th>
                <th className="px-4 py-2 border">{t("Teacher-Name")}</th>
                <th className="px-4 py-2 border">{t("Language")}</th>
                <th className="px-4 py-2 border">{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-gray-500">
                    {t("loading")}
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-red-500">
                    {error}
                  </td>
                </tr>
          ) : visibleSubjects.length > 0 ? (
                visibleSubjects.map((item, idx) => (
                  <tr
                    key={`${item._id || item.course || item.subject}-${idx}`}
                    className="hover:bg-gray-50 text-gray-800 odd:bg-white even:bg-gray-50/50"
                  >
                    <td className="px-4 py-2 border">{item.university}</td>
                    <td className="px-4 py-2 border">{item.faculty}</td>
                    <td className="px-4 py-2 border">{item.department}</td>
                    <td className="px-4 py-2 border">
                      {item.course || item.subject}
                    </td>
                    <td className="px-4 py-2 border">{item.semester}</td>
                    <td className="px-4 py-2 border">{item.credits}</td>
                    <td className="px-4 py-2 border">
                      {item.teacherName || item.teacher}
                    </td>
                    <td className="px-4 py-2 border">
                      {renderLang(item.lang)}
                    </td>
                    <td className="px-4 py-2 border">
                      <span className={getStatusColor(item.status)}>
                        {getStatusLabel(item.status)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4 text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <FaSearch className="text-gray-400" />
                      <span>{t("No-subjects-found")}</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </DialogBody>

      <DialogFooter className="sticky bottom-0 bg-white/95 backdrop-blur border-t px-2 py-2">
        <div className="flex flex-wrap justify-center items-center gap-2 w-full">
          <button
            className="px-2 py-1 text-xs border rounded hover:bg-indigo-50 disabled:opacity-50"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            {t("First")}
          </button>
          <button
            className="px-2 py-1 text-xs border rounded hover:bg-indigo-50 disabled:opacity-50"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("Prev")}
          </button>

          {/* Dynamic page numbers */}
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) =>
                p === 1 || p === totalPages || (p >= page - 2 && p <= page + 2) // show current ±2
            )
            .map((p, idx, arr) => (
              <React.Fragment key={p}>
                {idx > 0 && arr[idx] - arr[idx - 1] > 1 && (
                  <span className="px-1 text-xs">…</span>
                )}
                <button
                  className={`px-2 py-1 text-xs border rounded ${
                    p === page
                      ? "bg-indigo-500 text-white"
                      : "hover:bg-indigo-50"
                  }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              </React.Fragment>
            ))}

          <button
            className="px-2 py-1 text-xs border rounded hover:bg-indigo-50 disabled:opacity-50"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("Next")}
          </button>
          <button
            className="px-2 py-1 text-xs border rounded hover:bg-indigo-50 disabled:opacity-50"
            disabled={page === totalPages}
            onClick={() => setPage(totalPages)}
          >
            {t("Last")}
          </button>
        </div>
      </DialogFooter>
    </Dialog>
  );
};

export default ScheduledSubjectsModal;

